'use client';

/**
 * ReadabilityAdapter — makes 150+ pages of hard-coded inline styles readable
 * in BOTH themes without touching each page.
 *
 * Why: the pages set colours and font sizes inline (`style={{ color:'#475569',
 * fontSize:11 }}` — 3,500+ text nodes at 10–13px, ~2,000 at slate-600). CSS
 * cannot override an inline `font-size`, and dark mode used to rely on an
 * ever-growing list of attribute selectors that missed most colours, so
 * headings, table rows and form values disappeared.
 *
 * What it does (idempotent, reversible, cheap):
 *   1. Font floor: inline font sizes below 12.5px are raised to 12.5px
 *      (13px for 12px) — everything stays proportional, nothing wraps badly.
 *   2. Light mode: the three too-pale greys used for running text are lifted
 *      one step (#94a3b8 → #64748b, #cbd5e1 → #64748b) so hints are legible.
 *   3. Dark mode: any inline text colour that is DARK (luminance < 0.35) is
 *      mapped to a light equivalent of the same hue, any LIGHT background
 *      (luminance > 0.82) to a dark surface, and pale borders to soft slate.
 *      Elements on gradient/dark backgrounds are left alone.
 * Originals are stored on the element so switching back to light restores
 * them exactly. Runs on mount, on theme change, and on DOM mutations
 * (batched per animation frame).
 */

import { useEffect } from 'react';

const ORIG = 'olOrig'; // dataset key holding the original inline values (JSON)

function parseColor(v) {
  if (!v) return null;
  v = String(v).trim().toLowerCase();
  let m = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (m) {
    let h = m[1]; if (h.length === 3) h = h.split('').map(c => c + c).join('');
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
  }
  m = v.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] };
  if (v === 'white') return { r: 255, g: 255, b: 255, a: 1 };
  if (v === 'black') return { r: 0, g: 0, b: 0, a: 1 };
  return null;
}
function lum({ r, g, b }) {
  const f = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function rgbToHsl({ r, g, b }) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}
const hsl = (h, s, l, a = 1) => a >= 1 ? `hsl(${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}%)` : `hsl(${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}% / ${a})`;

/** Dark-theme replacement for a dark TEXT colour: same hue, lifted to a light tint. */
function liftText(c) {
  const { h, s, l } = rgbToHsl(c);
  if (s < 12 || l < 16) return '#e8edf7';                 // greys and near-blacks (slate-900 etc.) → near-white
  if (l < 30) return hsl(h, Math.min(70, s), 86, c.a);    // dark tinted text (slate-700/800, blue-900) → very light tint
  return hsl(h, Math.min(90, s + 10), 76, c.a);           // coloured text → pastel of same hue
}
/** Dark-theme replacement for a light BACKGROUND. */
function sinkBackground(c) {
  const { h, s, l } = rgbToHsl(c);
  if (l > 96 && s < 20) return '#111c3b';                 // white → card
  if (s < 20) return '#0f1a36';                           // slate-50/100 → well
  return hsl(h, Math.min(60, s), 22, c.a);                // tinted (blue-50, green-50…) → deep tint
}

// Keys in the normalized rgb() form the CSSOM reports (hex is never returned).
const LIGHT_LIFT = {
  'rgb(148, 163, 184)': '#64748b',   // slate-400 (2.5:1 on white) → slate-500
  'rgb(203, 213, 225)': '#64748b',   // slate-300 → slate-500
  'rgb(161, 161, 170)': '#52525b',   // zinc-400 → zinc-600
  'rgb(156, 163, 175)': '#6b7280',   // gray-400 → gray-500
  'rgb(184, 194, 214)': '#64748b',
};
const FONT_FLOOR = 12.5;

function readOrig(el) { try { return el.dataset[ORIG] ? JSON.parse(el.dataset[ORIG]) : null; } catch { return null; } }
function writeOrig(el, o) { try { if (o && Object.keys(o).length) el.dataset[ORIG] = JSON.stringify(o); else delete el.dataset[ORIG]; } catch {} }

function restore(el) {
  const o = readOrig(el); if (!o) return;
  for (const [prop, val] of Object.entries(o)) el.style.setProperty(prop, val);
  written.delete(el);
  writeOrig(el, null);
}

function insideDarkSurface(el) {
  // Walk up a few levels: if an ancestor carries a gradient or a dark inline
  // background, this text was designed for a dark panel — leave it alone.
  let n = el, depth = 0;
  while (n && n !== document.body && depth < 6) {
    if (n.style) {
      // Judge by the page's ORIGINAL background, not one we already darkened —
      // otherwise a parent adapted earlier in the same pass looks like a
      // designed-dark panel and its text is never lifted.
      const o = readOrig(n) || {};
      const bgImg = n.style.backgroundImage;
      const bgc = 'background-color' in o ? o['background-color'] : ('background' in o ? o['background'] : n.style.backgroundColor);
      const bgAny = bgImg || bgc || n.style.background;
      if (bgAny) {
        if (/gradient/i.test(bgAny)) return true;
        const c = parseColor(bgc || bgAny);
        if (c && c.a > 0.5 && lum(c) < 0.35) return true;
        if (c && c.a > 0.5) return false; // a light original surface — text on it is meant to be dark
      }
    }
    n = n.parentElement; depth++;
  }
  return false;
}

// element → { prop: normalized value we last wrote }. Used to tell our own
// style mutations apart from the page's (React) writes without counting
// mutation records, which browsers coalesce unpredictably.
const written = new WeakMap();
function noteWrite(el, prop) {
  const w = written.get(el) || {};
  w[prop] = el.style.getPropertyValue(prop);
  written.set(el, w);
}

function adapt(el, dark) {
  if (!el.style || el.closest('aside, .ol-no-adapt, [data-ol-skip]')) return;
  const orig = readOrig(el) || {};
  const before = JSON.stringify(orig);
  const set = (prop, val) => {
    if (el.style.getPropertyValue(prop) === val) return;
    if (!(prop in orig)) orig[prop] = el.style.getPropertyValue(prop);
    el.style.setProperty(prop, val);
    noteWrite(el, prop);
  };

  // 1. Font floor (both themes)
  const fs = el.style.fontSize;
  if (fs && /px$/.test(fs)) {
    const n = parseFloat(fs);
    if (n > 0 && n < FONT_FLOOR) set('font-size', (n >= 12 ? 13 : FONT_FLOOR) + 'px');
  }

  const color = el.style.color;
  const bgc = el.style.backgroundColor;
  const bg = el.style.background;
  const onDark = insideDarkSurface(el);

  if (!dark) {
    if (color && !onDark) {
      const lifted = LIGHT_LIFT[color.trim().toLowerCase()];
      if (lifted) set('color', lifted);
      // White text on a light (or no) background — the classic disabled-button
      // and "Kanban" toggle bug: invisible. Only when this element's OWN
      // background is light or absent (a dark parent is handled by onDark).
      const c = parseColor(color);
      const ownBg = parseColor(bgc || (bg && !/gradient|url\(/i.test(bg) ? bg : ''));
      const bgIsLight = ownBg ? (ownBg.a < 0.5 || lum(ownBg) > 0.6) : !/gradient/i.test(bg || '');
      if (c && c.a > 0.5 && lum(c) > 0.85) {
        if (bgIsLight) set('color', '#475569');
        // White label on a bright saturated button (emerald-500, green-500,
        // amber-400 …) is ~2:1 — deepen the button to the -700 step of its hue.
        else if (ownBg && ownBg.a > 0.6 && lum(ownBg) > 0.28) {
          const { h, s: sat } = rgbToHsl(ownBg);
          if (sat > 35) set(bgc ? 'background-color' : 'background', hsl(h, Math.min(sat, 80), 32, ownBg.a));
        }
      } else if (c && c.a > 0.5 && lum(c) > 0.36 && bgIsLight) {
        // Pale accent text (teal-300, amber-400, emerald-400 …) on a white card:
        // keep the hue, drop lightness until it reads (≈ the -700 step).
        const { h, s: sat } = rgbToHsl(c);
        if (sat > 25) set('color', hsl(h, Math.min(sat, 85), 30, c.a));
      }
    }
  } else {
    // 3. Dark theme: text
    const c = parseColor(color);
    if (c && c.a > 0.3 && lum(c) < 0.35 && !onDark && !/gradient/i.test(bg || '')) set('color', liftText(c));
    // Backgrounds (only solid, light ones — never gradients or images)
    const solid = bgc || (bg && !/gradient|url\(/i.test(bg) ? bg : '');
    const b = parseColor(solid);
    if (b && b.a > 0.6 && lum(b) > 0.82) set(bgc ? 'background-color' : 'background', sinkBackground(b));
    // Pale borders
    for (const prop of ['border-color', 'border-top-color', 'border-bottom-color', 'border-left-color', 'border-right-color']) {
      const bc = parseColor(el.style.getPropertyValue(prop));
      if (bc && bc.a > 0.4 && lum(bc) > 0.6) set(prop, 'rgba(148,163,184,0.32)');
    }
  }
  if (JSON.stringify(orig) !== before) writeOrig(el, orig);
}

function safeAdapt(el, dark) {
  try { adapt(el, dark); }
  catch (e) { try { (window.__olAdapterErrors = window.__olAdapterErrors || []).push(String(e && e.message || e)); } catch {} }
}
function run(root, dark) {
  const nodes = root.querySelectorAll ? root.querySelectorAll('[style]') : [];
  for (const el of nodes) safeAdapt(el, dark);
  if (root.getAttribute && root.getAttribute('style')) safeAdapt(root, dark);
}

export default function ReadabilityAdapter() {
  useEffect(() => {
    const html = document.documentElement;
    let dark = html.classList.contains('dark');
    let scheduled = null;
    const pending = new Set();

    const flush = () => {
      scheduled = null;
      const items = [...pending]; pending.clear();
      for (const n of items) { if (n.isConnected) run(n, dark); }
    };
    // setTimeout, not requestAnimationFrame: rAF is paused in background /
    // hidden windows (verified: it never fired in a backgrounded tab), which
    // left every element rendered after load un-adapted.
    const schedule = (n) => { pending.add(n); if (!scheduled) scheduled = setTimeout(flush, 24); };

    // Initial pass
    run(document.body, dark);

    // Theme switch: restore everything, then re-adapt for the new theme.
    const themeObs = new MutationObserver(() => {
      const now = html.classList.contains('dark');
      if (now === dark) return;
      dark = now;
      for (const el of document.body.querySelectorAll('[data-ol-orig]')) restore(el);
      run(document.body, dark);
    });
    themeObs.observe(html, { attributes: true, attributeFilter: ['class'] });

    // DOM changes: new nodes or inline style edits by React re-renders.
    const domObs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === 'childList') { for (const n of m.addedNodes) if (n.nodeType === 1) schedule(n); }
        else if (m.type === 'attributes' && m.target.nodeType === 1) {
          const el = m.target;
          const w = written.get(el);
          if (w) {
            // Did the page overwrite one of the properties WE manage? If every
            // managed prop still holds our value, this mutation is ours (or an
            // unrelated prop such as transform) — nothing to do.
            let touched = false;
            const o = readOrig(el) || {};
            for (const prop of Object.keys(w)) {
              if (el.style.getPropertyValue(prop) !== w[prop]) { touched = true; delete o[prop]; delete w[prop]; }
            }
            if (!touched) continue;
            writeOrig(el, o);
          }
          schedule(el);
        }
      }
    });
    domObs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });

    return () => { themeObs.disconnect(); domObs.disconnect(); if (scheduled) clearTimeout(scheduled); };
  }, []);
  return null;
}
